import { useCallback, useLayoutEffect, useRef } from "react";
import {
  shouldTransitionWidth,
  type WidthTransitionSnapshot,
  widthTransitionDurationMs,
} from "./useWidthTransition.logic";

/**
 * Eases an inline-sized element from its old width to its new one when its
 * content changes, so neighbours slide instead of jumping, and fades the new
 * content in over the same span. While it eases the element carries
 * `data-width-transition`, so its content can clip instead of truncating.
 * Resizes are recorded, never animated. Returns the ref callback.
 */
export function useWidthTransition(contentKey: string, resetKey: string) {
  const elementRef = useRef<HTMLElement | null>(null);
  const settledRef = useRef<(WidthTransitionSnapshot & { width: number }) | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);
  const keysRef = useRef<WidthTransitionSnapshot>({ contentKey, resetKey });
  keysRef.current = { contentKey, resetKey };

  const ref = useCallback((element: HTMLElement | null) => {
    elementRef.current = element;
    if (!element) return;
    const observer = new ResizeObserver(() => {
      if (cancelRef.current) return;
      settledRef.current = { ...keysRef.current, width: element.getBoundingClientRect().width };
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      cancelRef.current?.();
      elementRef.current = null;
      settledRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const next = { contentKey, resetKey };
    const previous = settledRef.current;
    const from = cancelRef.current ? element.getBoundingClientRect().width : previous?.width;
    cancelRef.current?.();
    const to = element.getBoundingClientRect().width;
    settledRef.current = { ...next, width: to };
    if (!shouldTransitionWidth(previous, next) || from === undefined || from === to) return;

    const timing = {
      duration: widthTransitionDurationMs(
        window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      ),
      easing: "ease-out",
    };
    const animations = [
      element.animate(
        [
          { width: `${from}px`, overflow: "hidden" },
          { width: `${to}px`, overflow: "hidden" },
        ],
        timing,
      ),
      ...Array.from(element.children, (child) =>
        child.animate([{ opacity: 0 }, { opacity: 1 }], timing),
      ),
    ];
    const settle = () => {
      if (cancelRef.current !== cancel) return;
      cancelRef.current = null;
      delete element.dataset.widthTransition;
    };
    const cancel = () => {
      for (const animation of animations) animation.cancel();
      settle();
    };
    cancelRef.current = cancel;
    element.dataset.widthTransition = "";
    void animations[0]?.finished.then(settle, () => {});
  }, [contentKey, resetKey]);

  return ref;
}
